import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Title } from "../../../../themes/Default/components/Title";
import { Main } from "../../../../themes/Default/components/Main";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../themes/Default/components/List";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../themes/Default/components/Form/components/Checkbox";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { Slide, toast, ToastContainer } from "react-toastify";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Title as ListTitle } from "../../../../themes/Default/components/List/components/Header/components/Title";
import { Controls } from "../../../../themes/Default/components/List/components/Header/components/Controls";
import { BodyAttendance } from "../../../../themes/Default/components/List/components/Body";
import { Toaster } from "../../../../themes/Default/components/Toaster";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";
import { groupTypes } from "../../../../services/utils/GroupTypes"

const Attendance = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 8;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const [totalRecords, setTotalRecords] = useState(0);
    const [filters, setFilters] = useState(defaultFilters);
    const isNextPageDisabled = (filters.startPage + 1) * recordsPerPage >= totalRecords;
    const navigate = useNavigate();
    const { groupId } = useParams();
    const { useViewGroupMutation, useListAttendancesMutation, useMarkAsCheckedMutation, useMarkAsUncheckedMutation, usePlansListMutation } = groupsManagement;
    const [attendancesList, { isLoading }] = useListAttendancesMutation();
    const [markAsChecked] = useMarkAsCheckedMutation();
    const [markAsUnchecked] = useMarkAsUncheckedMutation();
    const [dates, setDates] = useState();
    const [students, setStudents] = useState();
    const todayFormatted = new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
    const today = new Date().toISOString().split("T")[0];
    const [groupName, setGroupName] = useState("");
    const [groupCoordinatorUserId, setGroupCoordinatorUserId] = useState();
    const [group, setGroup] = useState();
    const [viewGroup] = useViewGroupMutation();
    const [shouldLoadNextPage, setShouldLoadNextPage] = useState(false);
    const hasCheckedDates = useRef(false);
    const [numOfPage, setNumOfPage] = useState(1);
    const [nonCancelledCountBefore, setNonCancelledCountBefore] = useState(0); 
    const [viewPlans] = usePlansListMutation();
    const [plans, setPlans] = useState([]);
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);

    const tabs = [
        { title: t("groups.attendance"), isActive: true },
        ...(hasPlan ? [
            { title: t("groups.planning"), onClick: () => gotoPlanningTab(groupId) },
        ] : []), 
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []), 
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []), 
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []),
        { title: t("documents"), onClick: () => gotoDocumentsTab(groupId) },
        { title: t("notes"), onClick: () => gotoNotesTab(groupId) },
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    const userId = decodedToken?.id;
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    };

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    };

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    };

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    };

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    };

    const gotoInformationAboutStudent = (studentId) => {
        navigate("/groups/" + groupId + "/information-about-student/" + studentId);
    };

    const gotoCancel = (cancelCalendarId) => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        navigate("/groups/" + groupId + "/cancel-class/" + cancelCalendarId);
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const isSubstitute = (dateId) => {
        return plans.some(plan => plan.dateId === dateId && plan.substituteProfessor !== null);
    }

    const handleChecked = (studentId, studentDateId) => {
        markAsChecked({ groupId: groupId, studentId: studentId, calendarId: studentDateId }).then((result) => {
            if (result?.error) {
            } else {
                toast.info(result?.data?.info?.responseMessage);
                updateStudentAttendance(studentId, studentDateId, true);
            }
        });
    };

    const handleUnchecked = (studentId, studentDateId) => {
        markAsUnchecked({ groupId: groupId, studentId: studentId, calendarId: studentDateId }).then((result) => {
            if (result?.error) {
            } else {
                toast.info(result?.data?.info?.responseMessage);
                updateStudentAttendance(studentId, studentDateId, false);
            }
        });
    };

    const updateStudentAttendance = (studentId, studentDateId, isPresent) => {
        setStudents((prevStudents) =>
            prevStudents.map((student) => {
                if (student.id !== studentId) return student;
                return {
                    ...student,
                    dates: student.dates.map((date) => (date.dateId === studentDateId ? { ...date, isPresent } : date)),
                };
            }),
        );
    };

    const onPageChange = (direction) => {
        setFilters((prevFilters) => {
            const newStartPage = direction === "prev" ? Math.max(prevFilters.startPage - 1, 0) : prevFilters.startPage + 1;
            if (direction === "prev" && numOfPage > 1) {
                setNumOfPage(numOfPage - recordsPerPage);
            } else if (direction === "next") {
                setNumOfPage(numOfPage + recordsPerPage);
            }

            if (newStartPage * recordsPerPage >= totalRecords) {
                return prevFilters;
            }

            return {
                ...prevFilters,
                startPage: newStartPage,
            };
        });
    };

    const loadGroupName = () => {
        viewGroup(groupId).then((result) => {
            setGroupName(result?.data?.name);
            setGroupCoordinatorUserId(result?.data?.coordinatorUserId);
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
            setGroup(result?.data);
        });
    };

    useEffect(() => {
        attendancesList({ groupId: groupId, body: filters }).then((result) => {
            setStudents(result?.data?.students);
            setDates(result?.data?.dates);
            setTotalRecords(result?.data?.totalRecords);
            setNonCancelledCountBefore(result?.data?.nonCancelledCountBefore);
        });
        loadGroupName();
        viewPlans(groupId).then((result) => {
            setPlans(result?.data?.records);
        });
    }, [groupId, attendancesList, filters]);

    useEffect(() => {
        if (shouldLoadNextPage) {
            onPageChange("next");
            setShouldLoadNextPage(false);
        }
    }, [shouldLoadNextPage]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/groups" />
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupName}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-user-group" />
                    <Link title={t("groups.attendance")} isActive={true} />
                </Breadcrumps>
                <List>
                    <ListHeader>
                        <ListTitle title={todayFormatted} />
                        <Controls onPageChange={onPageChange} isNextPageDisabled={isNextPageDisabled} />
                    </ListHeader>
                    <BodyAttendance>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell>
                                    <span>{t("groups.no")}</span>
                                </TableHeadCell>
                                <TableHeadCell width="150px">
                                    <span>{t("Status u grupi")}</span>
                                </TableHeadCell>
                                <TableHeadCell width="150px">
                                    <span>{t("groups.fullName")}</span>
                                </TableHeadCell>
                                <TableHeadCell width="auto">
                                    <span>{t("groups.canBePhotographed")}</span>
                                </TableHeadCell>
                                {group && [groupTypes.LETNJI_JEZICKI_KAMP, groupTypes.ZIMSKI_JEZICKI_KAMP, groupTypes.VIKEND_JEZICKI_KAMP].includes(group.typeName) && (
                                    <TableHeadCell>
                                        <span>{t("students.canGoHomeAlone")}</span>
                                    </TableHeadCell>
                                )}
                                {dates?.length > 0 &&
                                    dates?.map((date, index) => {
                                        const formattedDate = date.date.split(" ")[0];
                                        const dayOfWeek = new Date(formattedDate).toLocaleDateString("sr-Latn-RS", { weekday: "short" }).toUpperCase();
                                        const dayMonth = new Date(formattedDate).toLocaleDateString("sr-RS", { day: "2-digit", month: "2-digit" });
                                        
                                        const jsDate = new Date(formattedDate);
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);

                                        const isFutureOrToday = jsDate >= today;

                                        const nonCancelledCountInPage = dates
                                            .slice(0, index + 1)
                                            .filter(d => !d.isCancel)
                                            .length;

                                        const displayIndex = nonCancelledCountBefore + nonCancelledCountInPage;
                                        
                                        return (
                                            <TableHeadCell>
                                                
                                                <Button
                                                    icon="fa-solid fa-xmark"
                                                    color="#EB5757"
                                                    customClass={
                                                        !date.isCancel && (
                                                            decodedToken?.roles.includes(roles.SUPER_ADMIN) || 
                                                            (isFutureOrToday && (
                                                                groupCoordinatorUserId === userId ||
                                                                decodedToken?.roles.includes(roles.SECRETARY) ||
                                                                decodedToken?.roles.includes(roles.DIRECTOR) ||
                                                                decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
                                                            ))
                                                        ) 
                                                        ? "cancel-class__button" 
                                                        : "cancel-class__button hidden"
                                                    }
                                                    customSpanClass="cancel-class__span"
                                                    onClick={() => gotoCancel(date.id)}
                                                />
                                                                                               
                                                <p style={{ fontSize: "12px", color: "#828282", minHeight: "18px" }}>
                                                    {!date.isCancel ? displayIndex : ''}
                                                </p>                                                
                                                <span className="main-list__table-head-cell--dayOfWeek">{dayOfWeek}</span>
                                                <br></br>
                                                <span>{dayMonth}</span>
                                            </TableHeadCell>
                                        );
                                    })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {students?.length > 0 && (() => {
                            let activeIndex = 0;
                            return students.map((student) => {
                                if (!student.isArchived) activeIndex++;
                                return (
                                    <TableRow key={student.id}>
                                        <TableDataCell>{!student.isArchived ? String(activeIndex).padStart(2, "0") : ''}</TableDataCell>
                                        <TableDataCell>
                                            <span style={{ color: student.isArchived ? '#ff0000' : '#27ae60' }}>
                                                {student.isArchived ? t("Arhiviran") : t("Aktivan")}
                                            </span>
                                        </TableDataCell>
                                        <TableDataCell>
                                            {!student.isArchived ? (
                                                <Button 
                                                    title={student.name}
                                                    onClick={() => gotoInformationAboutStudent(student.id)}
                                                />
                                            ) : (
                                                <span style={{ color: '#ff0000' }}>
                                                    {student.name} {student.surname}
                                                </span>
                                            )}
                                        </TableDataCell>
                                        <TableDataCell>
                                            <span style={{ color: student.canBePhotographed ? '#27ae60' : '#ff0000' }}>
                                                {student.canBePhotographed ? t("yes") : t("no")}
                                            </span>
                                        </TableDataCell>
                                        {group && [groupTypes.LETNJI_JEZICKI_KAMP, groupTypes.ZIMSKI_JEZICKI_KAMP, groupTypes.VIKEND_JEZICKI_KAMP].includes(group.typeName) && (
                                            <TableDataCell>
                                                <span style={{ color: student.canGoHomeAlone ? '#27ae60' : '#ff0000' }}>
                                                    {student.canGoHomeAlone ? t("yes") : t("no")}
                                                </span>
                                            </TableDataCell>
                                        )}
                                        {student?.dates?.length > 0 &&
                                            student?.dates.map((studentDate) => {
                                                const substitute = isSubstitute(studentDate.dateId);
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);

                                                const yesterday = new Date(today);
                                                yesterday.setDate(yesterday.getDate() - 1);

                                                const date = new Date(studentDate.date);
                                                date.setHours(0, 0, 0, 0);

                                                const isBeforeYesterday = date < yesterday;
                                                const isFutureDate = date > today;

                                                return (
                                                    <TableDataCell key={studentDate.dateId} isToday={date.toDateString() === today.toDateString()} isSubstituteProfessor={substitute} isCancelClass={studentDate.isCancel}>
                                                        {!studentDate.isCancel && 
                                                        (!student.isArchived || 
                                                            (student.isArchived && (studentDate.isPresent === true || studentDate.isPresent === false))) && (
                                                            <Checkbox
                                                                name="isPresent"
                                                                pastDate={studentDate.isPresent === false}
                                                                checked={studentDate.isPresent}
                                                                disabled={
                                                                    isFutureDate ||
                                                                    (isBeforeYesterday && !decodedToken?.roles.includes(roles.SUPER_ADMIN)) ||
                                                                    student.isArchived
                                                                }
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        handleChecked(student.id, studentDate.dateId);
                                                                    } else {
                                                                        handleUnchecked(student.id, studentDate.dateId);
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </TableDataCell>
                                                );
                                            })}
                                    </TableRow>
                                );
                            });
                        })()}
                        </TableBody>
                    </BodyAttendance>
                </List>
            </Main>
        </DefaultLayout>
    );
};

export default Attendance;
